// src/monitoring/services/monitoringApi.js
//
// The Monitoring screens' only way to the backend. Every call is scoped to the
// saved project; the token comes from the shared auth session.

import { buildHeaders } from "../../services/api";

const base = () => {
  const b = process.env.REACT_APP_API_BASE?.replace(/\/$/, "");
  if (!b) throw new Error("The API address is not configured.");
  return b;
};

async function call(path, { method = "GET", body, accessToken, signal } = {}) {
  const response = await fetch(`${base()}${path}`, {
    method,
    headers: await buildHeaders(accessToken, body !== undefined ? { "Content-Type": "application/json" } : {}),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  if (response.status === 204) return null;
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = payload?.detail;
    const message = typeof detail === "string" ? detail : detail?.message || detail?.[0]?.msg || `Request failed (${response.status}).`;
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  return payload;
}

async function multipart(path, formData, { accessToken, signal } = {}) {
  const response = await fetch(`${base()}${path}`, {
    method: "POST",
    headers: await buildHeaders(accessToken),
    body: formData,
    signal,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = payload?.detail;
    const message = typeof detail === "string" ? detail : detail?.message || detail?.[0]?.msg || `Request failed (${response.status}).`;
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  return payload;
}

async function download(path, { accessToken } = {}) {
  const response = await fetch(`${base()}${path}`, { headers: await buildHeaders(accessToken) });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.detail || `Request failed (${response.status}).`);
  }
  return response.blob();
}

const q = (params) => {
  const s = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")).toString();
  return s ? `?${s}` : "";
};

export const fetchVendors = (o) => call("/monitoring/vendors", o);
export const fetchPortfolio = (projectId, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/portfolio`, o);
export const fetchSources = (projectId, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/sources`, o);
export const createSource = (projectId, body, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/sources`, { ...o, method: "POST", body });
export const updateSource = (sourceId, body, o) => call(`/monitoring/sources/${encodeURIComponent(sourceId)}`, { ...o, method: "PUT", body });
export const deleteSource = (sourceId, o) => call(`/monitoring/sources/${encodeURIComponent(sourceId)}`, { ...o, method: "DELETE" });
export const testSource = (sourceId, o) => call(`/monitoring/sources/${encodeURIComponent(sourceId)}/test`, { ...o, method: "POST" });
export const pollSource = (sourceId, o) => call(`/monitoring/sources/${encodeURIComponent(sourceId)}/poll`, { ...o, method: "POST" });
export const fetchParkDetail = (sourceId, day, o) => call(`/monitoring/sources/${encodeURIComponent(sourceId)}/detail${q({ day })}`, o);
export const fetchAlarms = (projectId, params, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/alarms${q(params)}`, o);
export const ackAlarm = (alarmId, o) => call(`/monitoring/alarms/${encodeURIComponent(alarmId)}/ack`, { ...o, method: "POST" });
export const resolveAlarm = (alarmId, o) => call(`/monitoring/alarms/${encodeURIComponent(alarmId)}/resolve`, { ...o, method: "POST" });
export const fetchAlarmRules = (projectId, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/alarm-rules`, o);
export const saveAlarmRules = (projectId, rules, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/alarm-rules`, { ...o, method: "PUT", body: rules });
export const fetchNotificationRules = (projectId, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/notification-rules`, o);
export const saveNotificationRules = (projectId, rules, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/notification-rules`, { ...o, method: "PUT", body: rules });
export const fetchReport = (projectId, month, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/report${q({ month })}`, o);
export const seedDemo = (projectId, days, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/demo-seed${q({ days })}`, { ...o, method: "POST" });
export const seedProjectSimulation = (projectId, body, o) => call(`/monitoring/projects/${encodeURIComponent(projectId)}/project-simulation`, { ...o, method: "POST", body });
export const downloadCsvReplayTemplate = (o) => download("/monitoring/csv-replay/template", o);
export const validateCsvReplay = (projectId, formData, o) => multipart(`/monitoring/projects/${encodeURIComponent(projectId)}/csv-replay/validate`, formData, o);
export const importCsvReplay = (projectId, formData, o) => multipart(`/monitoring/projects/${encodeURIComponent(projectId)}/csv-replay`, formData, o);
